import { createContext, FC, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import API from '../api/api';
import { EConsentStatus, TConsentPost } from '../api/types';
import { EQueryStringParam } from '../util/constants';
import GlobalContext from './GlobalContext';

type TConsentContext = {
    consentUiState: EConsentUiState;
    partnerName: string;
    consentScopes: TConsentScope[];
    setConsentScopes: (scopes: TConsentScope[]) => void;
    submitConsent: (consent: EConsentStatus) => void;
    redirectUrl: string;
};

type TConsentScope = {
    name: string;
    displayName: string;
    description: string;
    active: boolean;
    showDescription: boolean;
};

export enum EConsentUiState {
    default,
    queryStringParsed,
    loading,
    ready,
    success,
    fatalError,
}

const ConsentContext = createContext<TConsentContext | undefined>(undefined);

export const ConsentProvider: FC = (props) => {
    const { coBranding, setCoBranding, csrfToken, getCsrfToken } = useContext(GlobalContext);
    const [consentUiState, setConsentUiState] = useState(EConsentUiState.default);
    const [consentScopes, setConsentScopes] = useState<TConsentScope[]>([]);
    const [partnerName, setPartnerName] = useState('');
    const [returnUrl, setReturnUrl] = useState('');
    const [redirectUrl, setRedirectUrl] = useState('');
    const { search: queryParams } = useLocation();

    const parseQueryString = () => {
        const params = new URLSearchParams(queryParams);

        const returnUrl = params.get(EQueryStringParam.returnUrlCamelCase);
        setReturnUrl(returnUrl);
        if (returnUrl) {
            setConsentUiState(EConsentUiState.queryStringParsed);
        } else {
            setConsentUiState(EConsentUiState.fatalError);
            console.error(`Missing query string parameter -- ${EQueryStringParam.returnUrlCamelCase}`);
        }
    };

    const getSetupConsent = async () => {
        try {
            const response = await API.get.setupConsent(queryParams);
            const { clientName, clientLogoUrl, resourceScopes } = response.data;
            setCoBranding({
                ...coBranding,
                ...(clientLogoUrl && { logoUrl: clientLogoUrl }),
                name: clientName,
            });
            setPartnerName(clientName);
            setConsentScopes(resourceScopes.map((scope) => ({ ...scope, active: true, showDescription: false })));
            // csrfToken needed for next step in the workflow
            await getCsrfToken();
            setConsentUiState(EConsentUiState.ready);
        } catch {
            console.error('Failed to get setup consent');
            setConsentUiState(EConsentUiState.fatalError);
        }
    };

    const parseConsentedScopes = () => {
        const parsedScopes = consentScopes.reduce<string[]>((consentedScopes, currentScope) => {
            if (currentScope.active) {
                consentedScopes.push(currentScope.name);
            }
            return consentedScopes;
        }, []);
        return parsedScopes;
    };

    const submitConsent = async (consentStatus: EConsentStatus) => {
        const postObject: TConsentPost = {
            button: consentStatus,
            scopesConsented: consentStatus === EConsentStatus.continue ? parseConsentedScopes() : [],
            returnUrl,
        };
        try {
            setConsentUiState(EConsentUiState.loading);
            const response = await API.post.consent(postObject, csrfToken);
            setRedirectUrl(response.data.redirectUrl);
            setConsentUiState(EConsentUiState.success);
        } catch {
            console.error('Failed to submit consent');
            setConsentUiState(EConsentUiState.fatalError);
        }
    };

    useEffect(() => {
        if (consentUiState === EConsentUiState.default) {
            parseQueryString();
        }
        if (consentUiState === EConsentUiState.queryStringParsed) {
            getSetupConsent();
        }
    }, [consentUiState]);

    return (
        <ConsentContext.Provider
            value={{ consentUiState, partnerName, consentScopes, setConsentScopes, redirectUrl, submitConsent }}
        >
            {props.children}
        </ConsentContext.Provider>
    );
};

export default ConsentContext;
